/**
 * timeline.ts — the composition clock the hero animation is authored against.
 *
 * This is a faithful port of the primitives `hero-anim/animations-v3.jsx` exposes
 * as window globals in the Claude Design source (`useComposition`, `animate`,
 * `interpolate`, `Easing`, `clamp`). The design's hero is a PURE FUNCTION of one
 * authored-time axis `T` — nothing mounts or unmounts at a scene boundary, every
 * element interpolates through it — so reproducing the animation exactly means
 * reproducing these curves exactly, not approximating them with CSS keyframes or
 * a spring library.
 *
 * The easing formulas below are transcribed character-for-character from
 * `animations-v3.jsx` (including `easeOutCubic`'s `(--t)*t*t + 1`, which is
 * `(t-1)³+1`, and `easeOutBack`'s c1 = 1.70158). A "close enough" curve would
 * put the overshoot on every popped box in a visibly different place.
 *
 * The engine's dev-only surface — the scrubber, the tweaks panel, the export
 * harness, the watercolor helpers — is deliberately NOT ported. None of it
 * renders in the shipped animation.
 */

/** Cubic-bezier-free easing set, transcribed from `animations-v3.jsx`. */
export const Easing = {
  linear: (t: number): number => t,
  /** `(--t) * t * t + 1` — i.e. (t-1)³ + 1. */
  easeOutCubic: (t: number): number => {
    const u = t - 1;
    return u * u * u + 1;
  },
  easeInOutCubic: (t: number): number =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
  easeInOutSine: (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2,
  /** Overshoot. c1 = 1.70158, c3 = c1 + 1. */
  easeOutBack: (t: number): number => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
} as const;

export type EaseFn = (t: number) => number;

export const clamp = (v: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, v));

export interface AnimateSpec {
  from?: number;
  to?: number;
  start?: number;
  end?: number;
  ease?: EaseFn;
}

/**
 * `animate({from, to, start, end, ease})(T)` — a single eased segment, held at
 * `from` before `start` and at `to` after `end`.
 */
export function animate({
  from = 0,
  to = 1,
  start = 0,
  end = 1,
  ease = Easing.easeInOutCubic,
}: AnimateSpec): (t: number) => number {
  return (t: number): number => {
    if (t <= start) return from;
    if (t >= end) return to;
    const local = (t - start) / (end - start);
    return from + (to - from) * ease(local);
  };
}

/**
 * `interpolate(stops, values, ease | ease[])(T)` — a piecewise ramp. When
 * `ease` is an array it is indexed per SEGMENT, so `ease[i]` governs the span
 * from `stops[i]` to `stops[i+1]`; a missing entry falls back to linear. The
 * hero's camera zoom depends on exactly this per-segment behaviour.
 */
export function interpolate(
  input: readonly number[],
  output: readonly number[],
  ease: EaseFn | readonly EaseFn[] = Easing.linear,
): (t: number) => number {
  return (t: number): number => {
    const first = input[0];
    const last = input[input.length - 1];
    if (first === undefined || last === undefined) return 0;
    if (t <= first) return output[0] ?? 0;
    if (t >= last) return output[output.length - 1] ?? 0;
    for (let i = 0; i < input.length - 1; i += 1) {
      const a = input[i];
      const b = input[i + 1];
      if (a === undefined || b === undefined) continue;
      if (t >= a && t <= b) {
        const span = b - a;
        const local = span === 0 ? 0 : (t - a) / span;
        const easeFn = Array.isArray(ease)
          ? ((ease as readonly EaseFn[])[i] ?? Easing.linear)
          : (ease as EaseFn);
        const eased = easeFn(local);
        const oa = output[i] ?? 0;
        const ob = output[i + 1] ?? 0;
        return oa + (ob - oa) * eased;
      }
    }
    return output[output.length - 1] ?? 0;
  };
}

/**
 * The scene table, verbatim from `window.OM_SCENES` in the design source.
 * `CUES` is the running sum of these durations — the engine derives it the same
 * way, so the two cannot drift.
 */
export const SCENES = [
  { name: "Groundwork", dur: 1.6 },
  { name: "Framing", dur: 2.2 },
  { name: "Details", dur: 2.6 },
  { name: "Landscaping", dur: 2 },
  { name: "Hold", dur: 2 },
  { name: "Reset", dur: 1.2 },
] as const;

export type CueName = (typeof SCENES)[number]["name"];

/** Authored start time of each scene, in seconds. */
export const CUES: Record<CueName, number> = (() => {
  const out = {} as Record<CueName, number>;
  let acc = 0;
  for (const s of SCENES) {
    out[s.name] = acc;
    acc += s.dur;
  }
  return out;
})();

/** Total authored length — 11.6s. `window.OM_PLAYBACK` is `{"mode":"loop"}`. */
export const AUTHORED_TOTAL = SCENES.reduce((a, s) => a + s.dur, 0);
